import { ReactNode, useEffect, useState } from 'react';
import { Card, Checkbox, SignaturePad } from '@popsure/dirty-swan';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { Header } from '../../Screen';
import Markdown from '../../markdown';

import styles from './styles.module.scss';

export type SignatureFormProps<Questionnaire extends QuestionnaireQuestions> = {
  checkboxOptions?: Record<string, { title: string; description?: string}>;
} & QuestionnaireFormProps<Questionnaire, string>;

export const SignatureForm = <Questionnaire extends QuestionnaireQuestions>({
  value,
  onSubmitValue,
  question,
  featureName,
  setValidForSubmission,
  checkboxOptions,
}: SignatureFormProps<Questionnaire>) => {
  const [signature, setSignature] = useState<string>('');
  const [checkboxValues, setCheckboxValues] = useState<string[]>([]);

  const options = Object.entries(checkboxOptions || {}).reduce(
    (acc, [key, { title, description }]) => {
      acc[key] = { title: <Markdown>{title}</Markdown>, description };
      return acc;
    },
    {} as Record<string, { title: ReactNode; description?: string }>
  );

  const handleSubmit = () => {
    if (!signature) {
      throw new Error(
        `[${featureName}] Input value is missing for submission for question: ${question}`
      );
    }
    onSubmitValue(signature);
  };

  useEffect(() => {
    const allOptions = Object.keys(checkboxOptions || {});
    const areAllOptionsChecked = allOptions.every((option) => 
      checkboxValues.includes(option)
    );
    
    const isValid = signature && areAllOptionsChecked;
    setValidForSubmission(Boolean(isValid));
  }, [checkboxOptions, checkboxValues, setValidForSubmission, signature]);

  return (
    <Header onSubmit={handleSubmit}>
      <Card classNames={{wrapper: 'wmx10'}}>
        <SignaturePad onChange={setSignature} />
        
        {checkboxOptions && (
          <Checkbox
            bordered={false}
            wide
            classNames={{
              container: 'mt24 w100',
              label: styles.checkboxLabel,
            }}
            onChange={setCheckboxValues}
            options={options}
            value={checkboxValues}
          />
        )}
      </Card>
    </Header>
  );
};
